import React, {
  useEffect,
  useRef,
  type RefObject,
} from 'react';
import classNames from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Lottie, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import { SCREEN_MD } from '../../../config/constants';
import MediaPlayerSuspense from '../../../components/MediaPlayer';
import { ButtonWrapper, ParagraphsRenderer, VariableRatiosPicture } from '../../../components';
import type { NodeHomePage as Type } from '../../../types/uncinc_profile/nodes';

import arrowDownLottie from '../../../assets/lotties/arrow-down.json';

import './style.scss';

interface HomePageProps {
  entity: ImmutableEntity<Type>;
  className?: string;
}

const NodeHomePage: React.FC<HomePageProps> = ({
  entity,
  className,
}) => {
  const homePage = entity.toJS();
  const heroTitle = homePage.title[0].value;
  const heroImage = homePage.field_media_item[0];
  const heroVideoMobile = homePage.field_hero_video_mobile[0];
  const heroVideoDesktop = homePage.field_hero_video_desktop[0];

  const link = homePage.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const linkSecondary = homePage.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const classes = classNames('Node', 'NodeHomePage', className);

  const { width } = useWindowDimensions(true);
  const mediaPlayerClassName = classNames({
    NodeHomePage__hero__media__videoMobile: width < SCREEN_MD,
    NodeHomePage__hero__media__videoDesktop: width >= SCREEN_MD,
  });

  const lottieRef: RefObject<HTMLDivElement> = useRef(null);

  useEffect(() => {
    // Hide the Lottie when the user is scrolling.
    const hideLottie = () => {
      if (window.scrollY > 100 && lottieRef.current) {
        lottieRef.current.classList.add('hideLottie');
        window.removeEventListener('scroll', hideLottie);
      }
    };

    window.addEventListener('scroll', hideLottie);

    return () => window.removeEventListener('scroll', hideLottie);
  }, []);

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeHomePage__hero">
        <div className="NodeHomePage__hero__media">
          {(!heroVideoMobile || !heroVideoDesktop) && heroImage && (
            <VariableRatiosPicture
              image={heroImage}
              aspectRatios={['9_16', '3_4', '4_3', '16_9', '3_1']}
              cropSizes={['sm', 'md', 'lg', 'lg', 'xl']}
            />
          )}
          {heroVideoMobile && heroVideoDesktop && (
            <div className={mediaPlayerClassName}>
              <MediaPlayerSuspense
                src={width < SCREEN_MD ? heroVideoMobile?.source : heroVideoDesktop?.source}
                type="video/mp4"
                options={{
                  autoplay: true,
                  muted: true,
                  loop: true,
                  controls: false,
                }}
              />
            </div>
          )}
        </div>
        <div className="NodeHomePage__hero__containerWrapper">
          <Container>
            <div className="row">
              <div className="col-12 col-md-9 col-lg-9 col-xl-7">
                <div className="NodeHomePage__hero__text-wrapper">
                  <h1 itemProp="title" className="NodeHomePage__hero__title asH1-XL">{heroTitle}</h1>
                  {(link || linkSecondary) && (
                    <div className="NodeHomePage__hero__links">
                      {link && (
                        <ButtonWrapper
                          inverted
                          type="secondary"
                          href={linkUrl}
                        >
                          {linkTitle}
                        </ButtonWrapper>
                      )}
                      {linkSecondary && (
                        <ButtonWrapper
                          inverted
                          type="textual"
                          href={linkSecondaryUrl}
                        >
                          {linkSecondaryTitle}
                        </ButtonWrapper>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Container>
        </div>
        <div ref={lottieRef} className="NodeHomePage__hero__lottie">
          <Lottie
            // @ts-ignore
            id="arrowDown"
            data={arrowDownLottie}
            autoPlay
            loop
            direction={1}
            speed={0.8}
          />
        </div>
      </div>
      <ParagraphsRenderer className="NodeHomePage__paragraphs" paragraphs={entity.get('field_paragraphs')} isWide />
    </article>
  );
};

export default NodeHomePage;
